import React, { useEffect, useState } from 'react';
import axios from 'axios';

const ClassDetailPage = ({ classId, onBack }) => {
  const [classData, setClassData] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', year: '', category: '', description: '' });
  const [newDeckTitle, setNewDeckTitle] = useState('');
  const [deckSelection, setDeckSelection] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchClassData = async () => {
    try {
      const response = await axios.get(`https://amora-backend.onrender.com/api/classes/${classId}`);
      setClassData(response.data);
    } catch (err) {
      console.error('Error fetching class:', err);
    }
  };

  useEffect(() => {
    if (classId) fetchClassData();
  }, [classId]);

  const handleChange = (e) => setNewEvent({ ...newEvent, [e.target.name]: e.target.value });

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const eventWithId = { ...newEvent, _id: `${Date.now()}` };

    try {
      await axios.post(`https://amora-backend.onrender.com/api/classes/${classId}/events`, eventWithId);
      setNewEvent({ title: '', year: '', category: '', description: '' });
      fetchClassData();
    } catch (err) {
      console.error('Failed to add event:', err);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/classes/${classId}/events/${eventId}`);
      fetchClassData();
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const handleCreateDeck = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`https://amora-backend.onrender.com/api/classes/${classId}/decks`, { title: newDeckTitle });
      setNewDeckTitle('');
      fetchClassData();
    } catch (err) {
      console.error('Failed to create deck:', err);
    }
  };

  const handleDeckSelect = (eventId, value) => {
    setDeckSelection((prev) => ({ ...prev, [eventId]: value }));
  };

  const handleAddToDeck = async (eventId) => {
    const event = classData.events.find((e) => e._id === eventId);
    const selected = deckSelection[eventId];

    if (!selected || !event) return;

    try {
      await axios.post(`https://amora-backend.onrender.com/api/classes/${classId}/decks/${selected}/events`, event);
      fetchClassData();
    } catch (err) {
      console.error('Failed to add to deck:', err);
    }
  };

  const handleDeleteDeck = async (deckTitle) => {
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/classes/${classId}/decks/${deckTitle}`);
      fetchClassData();
    } catch (err) {
      console.error('Failed to delete deck:', err);
    }
  };

  const handleRemoveFromDeck = async (deckTitle, eventId) => {
    try {
      await axios.delete(`https://amora-backend.onrender.com/api/classes/${classId}/decks/${deckTitle}/events/${eventId}`);
      fetchClassData();
    } catch (err) {
      console.error('Failed to remove from deck:', err);
    }
  };

  const openModal = async (event) => {
    setSelectedEvent(event);
    setInsight(null);
    setLoading(true);
    try {
      const res = await axios.post('https://amora-ai-service.onrender.com/research-assistant', {
        title: event.title,
        description: event.description,
      });
      setInsight(res.data);
    } catch (err) {
      console.error('Failed to fetch insights:', err);
      setInsight({ ai_summary: '⚠️ Failed to fetch insights.' });
    }
    setLoading(false);
  };

  const closeModal = () => {
    setSelectedEvent(null);
    setInsight(null);
  };

  if (!classData) return <div className="p-8 text-center">Loading class...</div>;

  return (
    <div className="min-h-screen bg-gray-100 text-black p-6">
      <button
        onClick={onBack}
        className="mb-4 text-blue-600 underline hover:text-blue-800"
      >
        ← Back to My Classes
      </button>

      <h1 className="text-3xl font-bold text-blue-900 mb-6">{classData.title}</h1>

      {/* Event and Deck UI... (same as before) */}

      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl relative">
            <button
              onClick={closeModal}
              className="absolute top-3 right-4 text-white text-xl font-bold"
            >✖</button>

            <h3 className="text-2xl font-bold text-purple-300 mb-2">{selectedEvent.title}</h3>
            <p className="text-sm text-gray-400 mb-1">📅 {selectedEvent.year}</p>
            <p className="text-sm text-gray-200 mb-4">{selectedEvent.description}</p>

            <h4 className="text-lg font-semibold text-white mb-2">🧠 AI Summary</h4>
            <p className="text-sm text-gray-300 whitespace-pre-wrap mb-4">
              {loading ? '⏳ Generating summary...' : insight?.ai_summary}
            </p>

            {insight?.did_you_know && (
              <p className="text-sm text-yellow-300 italic mb-4">{insight.did_you_know}</p>
            )}

            <h4 className="text-sm font-semibold text-white mb-2">🔗 External Resources</h4>
            <ul className="text-blue-400 text-sm space-y-1 mb-4">
              {insight?.related_links?.map((link, i) => (
                <li key={i}>
                  <a href={link.url} target="_blank" rel="noreferrer" className="underline hover:text-blue-200">
                    {link.title}
                  </a>
                </li>
              ))}
            </ul>

            <h4 className="text-sm font-semibold text-white mb-1">🔍 Try Searching:</h4>
            <ul className="list-disc list-inside text-sm text-gray-400">
              {insight?.recommended_searches?.map((term, i) => (
                <li key={i}>{term}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassDetailPage;
